import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Alert, Button, ThemeMenu } from '@easydent/shared-ui';
import { forgotPassword } from '@/lib/api';

/**
 * Asks for a password-reset link.
 *
 * <p>⚠️ The confirmation deliberately does NOT say whether the address is known. The API answers the
 * same way either way — telling the visitor apart from that would hand anyone a free way to test
 * which addresses hold operator accounts — and this screen has to keep that promise, or the careful
 * bit on the server is undone by the wording here.</p>
 */
const ForgotPasswordPage: React.FC = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await forgotPassword(email.trim());
    } finally {
      // Shown whatever happened, for the same reason the API always returns 204.
      setSent(true);
      setBusy(false);
    }
  };

  const field = 'w-full rounded-lg border border-line-strong px-3 py-2 text-sm focus:border-brand-500 focus:outline-none';

  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas p-6">
      <div className="w-full max-w-sm rounded-2xl border border-line bg-surface p-8 shadow-sm">
        <div className="mb-4 flex justify-end">
          <ThemeMenu labels={{ label: t('theme.label', 'Theme'), light: t('theme.light', 'Light'), dark: t('theme.dark', 'Dark'), system: t('theme.system', 'System') }} />
        </div>
        <div className="mb-6 flex items-center gap-2 text-xl font-bold text-brand-ink-strong">
          <span>⬡</span> easyDent <span className="font-normal text-subtle-ink">{t('portalName', 'Operator')}</span>
        </div>

        {sent ? (
          <>
            <Alert variant="success" className="mb-4">
              {t('forgot.sent', 'If that address has an account, a reset link is on its way. It expires in an hour.')}
            </Alert>
            <Link to="/login" className="text-sm font-medium text-brand-ink-strong hover:underline">
              {t('forgot.backToSignIn', 'Back to sign in')}
            </Link>
          </>
        ) : (
          <form onSubmit={submit}>
            <p className="mb-5 text-sm text-muted">
              {t('forgot.intro', 'Enter your email and we will send you a link to set a new password.')}
            </p>
            <label className="mb-1 block text-xs uppercase tracking-wide text-subtle-ink">
              {t('login.email', 'Email')}
            </label>
            <input
              type="email" autoComplete="username" autoFocus required
              className={`${field} mb-5`} value={email} onChange={(e) => setEmail(e.target.value)}
            />
            <Button type="submit" disabled={busy} className="w-full">
              {busy ? t('forgot.sending', 'Sending…') : t('forgot.submit', 'Send the link')}
            </Button>
            <div className="mt-4 text-center">
              <Link to="/login" className="text-sm text-subtle-ink hover:text-muted-strong">
                {t('forgot.backToSignIn', 'Back to sign in')}
              </Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
